import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Modal, Form, Input, Select, DatePicker, InputNumber, message, Divider, Row, Col, Card, Descriptions, Tag, Tooltip, Empty, Typography, Alert } from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined, SearchOutlined, ClearOutlined, FilterOutlined, EyeOutlined, ReloadOutlined, SwapOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { matriculaService, alunoService, unidadeService, turmaService, serieService, parcelaService, contratoService, responsavelService } from '../services/api';
import { fetchAllPaginated } from '../helpers/fetchAllPaginated';
import { handleApiError } from '../utils/apiError';
import { formatCPF } from '../utils/documento';
import { useAuth } from '../contexts/AuthContext';
import dayjs from 'dayjs';
import ShortId from './common/ShortId';

const MATRICULA_STATUS_OPTIONS = [
  { label: 'Ativa', value: 'ATIVA' },
  { label: 'Pendente', value: 'PENDENTE' },
  { label: 'Cancelada', value: 'CANCELADA' },
];

const CONTRATO_TEMPLATE_OPTIONS = [
  { label: 'Contrato com desconto', value: 'MjA5NzU4' },
  { label: 'Contrato com multa', value: 'MjA5NzU5' },
];

const STATUS_CONTRATO_PRINCIPAL_ATIVO = ['GERADO', 'ENVIADO', 'ASSINADO'];
const RESPONSAVEL_CENTRAL_INDISPONIVEL = 'Responsável financeiro não disponível no cadastro central';
const ENDERECO_CENTRAL_INDISPONIVEL = 'Endereço do responsável não disponível no cadastro central';

const MatriculasList = () => {
  const { user } = useAuth();
  const [matriculas, setMatriculas] = useState([]);
  const [matriculasFiltradas, setMatriculasFiltradas] = useState([]);
  const [alunos, setAlunos] = useState([]);
  const [unidades, setUnidades] = useState([]);
  const [turmas, setTurmas] = useState([]);
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [statusOriginalEdicao, setStatusOriginalEdicao] = useState(null);
  const [mostrarFiltros, setMostrarFiltros] = useState(true);
  const [parcelasModalVisible, setParcelasModalVisible] = useState(false);
  const [parcelasLoading, setParcelasLoading] = useState(false);
  const [parcelas, setParcelas] = useState([]);
  const [parcelasActionLoading, setParcelasActionLoading] = useState(false);
  const [matriculaSelecionada, setMatriculaSelecionada] = useState(null);
  const [contratosPrincipaisAtivos, setContratosPrincipaisAtivos] = useState({});
  const [contratoModalVisible, setContratoModalVisible] = useState(false);
  const [contratoTemplateSelecionado, setContratoTemplateSelecionado] = useState(CONTRATO_TEMPLATE_OPTIONS[0].value);
  const [matriculaContratoSelecionada, setMatriculaContratoSelecionada] = useState(null);
  const [gerarContratoLoading, setGerarContratoLoading] = useState(false);
  const [contratoPreviewModalVisible, setContratoPreviewModalVisible] = useState(false);
  const [contratoPreviewLoading, setContratoPreviewLoading] = useState(false);
  const [contratoPreviewData, setContratoPreviewData] = useState(null);
  const [contratoPreviewError, setContratoPreviewError] = useState(null);
  const [trocaVigenciaModalVisible, setTrocaVigenciaModalVisible] = useState(false);
  const [trocaVigenciaLoading, setTrocaVigenciaLoading] = useState(false);
  const [matriculaTrocaSelecionada, setMatriculaTrocaSelecionada] = useState(null);
  const [alterarVencimentoModalVisible, setAlterarVencimentoModalVisible] = useState(false);
  const [alterarVencimentoLoading, setAlterarVencimentoLoading] = useState(false);
  const [matriculaVencimentoSelecionada, setMatriculaVencimentoSelecionada] = useState(null);
  const [corrigirVigenciaInicialModalVisible, setCorrigirVigenciaInicialModalVisible] = useState(false);
  const [corrigirVigenciaInicialLoading, setCorrigirVigenciaInicialLoading] = useState(false);
  const [matriculaCorrecaoSelecionada, setMatriculaCorrecaoSelecionada] = useState(null);
  const [detalhesModalVisible, setDetalhesModalVisible] = useState(false);
  const [detalhesLoading, setDetalhesLoading] = useState(false);
  const [matriculaDetalhes, setMatriculaDetalhes] = useState(null);
  const [responsavelFinanceiroPorAluno, setResponsavelFinanceiroPorAluno] = useState({});
  const [form] = Form.useForm();
  const [formTrocaVigencia] = Form.useForm();
  const [formAlterarVencimento] = Form.useForm();
  const [formCorrigirVigenciaInicial] = Form.useForm();
  const statusEdicaoAtual = (Form.useWatch('status', form) || statusOriginalEdicao || '').toUpperCase();
  const podeAlterarVencimentoNaEdicao = Boolean(editingId) && statusEdicaoAtual === 'ATIVA';
  const trocaDataInicio = Form.useWatch('data_inicio', formTrocaVigencia);
  const trocaDataFim = Form.useWatch('data_fim', formTrocaVigencia);
  const trocaValorTotal = Form.useWatch('valor_total', formTrocaVigencia);
  const trocaValorMensal = Form.useWatch('valor_mensal', formTrocaVigencia);
  const trocaQtdParcelasRestantes = Form.useWatch('qtd_parcelas_restantes', formTrocaVigencia);
  const trocaDescontoPercentual = Form.useWatch('desconto_percentual', formTrocaVigencia);
  const trocaEhMensalidade = ((matriculaTrocaSelecionada?.tipo_matricula || 'ANUAL').toUpperCase() === 'MENSALIDADE');
  const trocaQtdCompetenciasCalculada =
    trocaDataInicio && trocaDataFim && dayjs(trocaDataFim).isValid() && dayjs(trocaDataInicio).isValid()
      ? (dayjs(trocaDataFim).diff(dayjs(trocaDataInicio), 'month') + 1)
      : null;
  const valorParcelaCalculadoTroca =
    trocaEhMensalidade
      ? (Number(trocaValorMensal) > 0 ? Number(trocaValorMensal) : null)
      : (Number(trocaValorTotal) > 0 && Number(trocaQtdParcelasRestantes) > 0
      ? Number(trocaValorTotal) / Number(trocaQtdParcelasRestantes)
      : null);
  const parcelaFinalCalculadaTroca =
    valorParcelaCalculadoTroca !== null
      ? valorParcelaCalculadoTroca * (1 - ((Number(trocaDescontoPercentual) || 0) / 100))
      : null;
  const alunosOrdenados = [...alunos].sort((a, b) =>
    (a.nome_aluno || '').localeCompare(b.nome_aluno || '')
  );
  const [filtros, setFiltros] = useState({
    id: '',
    ano_letivo: null,
    id_alunos: null,
    id_unidades: null,
    id_turma: null,
    status: null,
    busca_geral: '',
  });
  const [filtrosAplicados, setFiltrosAplicados] = useState({
    id: '',
    ano_letivo: null,
    id_alunos: null,
    id_unidades: null,
    id_turma: null,
    status: null,
    busca_geral: '',
  });
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });

  useEffect(() => {
    fetchMatriculas();
    fetchAlunos();
    fetchResponsaveisFinanceirosCentrais();
    fetchUnidades();
    fetchTurmas();
    fetchSeries();
  }, []);

  const fetchResponsaveisFinanceirosCentrais = async () => {
    try {
      const response = await responsavelService.list();
      const responsaveis = Array.isArray(response?.data) ? response.data : [];
      const mapa = {};

      responsaveis.forEach((responsavel) => {
        const alunosVinculados = Array.isArray(responsavel?.alunos) ? responsavel.alunos : [];
        alunosVinculados.forEach((vinculoAluno) => {
          const alunoId = vinculoAluno?.id;
          if (!alunoId) return;

          const pontuacao = vinculoAluno?.is_responsavel_financeiro ? 2 : 1;
          const atual = mapa[alunoId];
          if (atual && atual.pontuacao >= pontuacao) return;

          mapa[alunoId] = {
            pontuacao,
            nome: responsavel?.nome_completo || null,
            cpf: responsavel?.cpf || null,
            email: responsavel?.email || null,
            telefone: responsavel?.telefone || null,
            endereco: responsavel?.endereco || null,
            numero: responsavel?.numero || null,
            complemento: responsavel?.complemento || null,
            bairro: responsavel?.bairro || null,
            cep: responsavel?.cep || null,
          };
        });
      });

      setResponsavelFinanceiroPorAluno(mapa);
    } catch (error) {
      console.error('Erro ao carregar responsáveis financeiros centralizados', error);
      setResponsavelFinanceiroPorAluno({});
    }
  };

  const formatCurrency = (valor) => {
    if (valor === null || valor === undefined) return '-';
    const numeric = Number(valor);
    if (Number.isNaN(numeric)) {
      return '-';
    }
    return `R$ ${numeric.toFixed(2)}`;
  };

  const formatDate = (value) => (value ? dayjs(value).format('DD/MM/YYYY') : '-');

  const extrairErroContratoPreview = (error) => {
    const detail = error?.response?.data?.detail;
    if (typeof detail === 'string' && detail.trim()) {
      return { message: detail.trim(), missingFields: [] };
    }
    if (detail && typeof detail === 'object') {
      return {
        message: detail.message || 'Nao foi possivel montar preview do contrato.',
        missingFields: Array.isArray(detail.missing_fields) ? detail.missing_fields : [],
      };
    }
    return {
      message: 'Nao foi possivel montar preview do contrato.',
      missingFields: [],
    };
  };

  const carregarPreviewContrato = async (matriculaId) => {
    setContratoPreviewLoading(true);
    setContratoPreviewData(null);
    setContratoPreviewError(null);
    try {
      const response = await contratoService.previewPorMatricula(matriculaId);
      setContratoPreviewData(response?.data || {});
    } catch (error) {
      setContratoPreviewError(extrairErroContratoPreview(error));
    } finally {
      setContratoPreviewLoading(false);
    }
  };

  const formatEnderecoResponsavelPreview = (preview) => {
    if (!preview) return ENDERECO_CENTRAL_INDISPONIVEL;
    const partes = [
      preview.endereco,
      preview.numero_residencia,
      preview.complemento,
      preview.bairro,
      preview.cep ? `CEP ${preview.cep}` : null,
    ].filter(Boolean);
    return partes.length ? partes.join(', ') : ENDERECO_CENTRAL_INDISPONIVEL;
  };

  const showSkippedPastParcelasModal = (payload) => {
    const count = payload?.skipped_past_parcelas_count || 0;
    if (!count) return;
    const lista = payload.skipped_past_parcelas || [];
    const resumo = lista.slice(0, 5);
    const restante = lista.length - resumo.length;
    const mensagem = payload.user_message || 'Parcelas vencidas não foram geradas automaticamente.';

    Modal.info({
      title: 'Parcelas vencidas não geradas',
      width: 520,
      content: (
        <div>
          <Typography.Paragraph>{mensagem}</Typography.Paragraph>
          {resumo.length > 0 && (
            <ul style={{ paddingLeft: 20 }}>
              {resumo.map((item) => (
                <li key={`${item.numero}-${item.data_vencimento}`}>
                  Parcela #{item.numero} — venc. {formatDate(item.data_vencimento)} — valor {formatCurrency(item.valor)}
                </li>
              ))}
            </ul>
          )}
          {restante > 0 && (
            <Typography.Text type="secondary">
              +{restante} parcela(s) adicionais não foram geradas
            </Typography.Text>
          )}
        </div>
      ),
    });
  };

  const fetchParcelasDaMatricula = async (matriculaId) => {
    setParcelasLoading(true);
    try {
      const response = await matriculaService.listParcelas(matriculaId);
      const parcelasOrdenadas = [...(response.data || [])].sort((a, b) => {
        const competenciaA = a?.competencia || '';
        const competenciaB = b?.competencia || '';
        if (competenciaA !== competenciaB) {
          return competenciaA.localeCompare(competenciaB);
        }

        const vencimentoA = a?.data_vencimento ? dayjs(a.data_vencimento).valueOf() : Number.POSITIVE_INFINITY;
        const vencimentoB = b?.data_vencimento ? dayjs(b.data_vencimento).valueOf() : Number.POSITIVE_INFINITY;
        if (vencimentoA !== vencimentoB) {
          return vencimentoA - vencimentoB;
        }

        const numeroA = Number(a?.numero_parcela || 0);
        const numeroB = Number(b?.numero_parcela || 0);
        if (numeroA !== numeroB) {
          return numeroA - numeroB;
        }

        return Number(a?.id || 0) - Number(b?.id || 0);
      });
      setParcelas(parcelasOrdenadas);
    } catch (error) {
      message.error('Erro ao carregar parcelas desta matrícula');
      console.error(error);
    }
    setParcelasLoading(false);
  };

  const openParcelasModal = (record) => {
    setMatriculaSelecionada(record);
    setParcelasModalVisible(true);
    fetchParcelasDaMatricula(record.id);
  };

  const openDetalhesModal = async (record) => {
    if (!record?.id) return;
    setDetalhesModalVisible(true);
    setDetalhesLoading(true);
    setMatriculaDetalhes(null);
    try {
      const response = await matriculaService.getDetalhes(record.id);
      setMatriculaDetalhes(response?.data || null);
    } catch (error) {
      handleApiError(error, 'Erro ao carregar detalhes da matrícula');
      setDetalhesModalVisible(false);
    } finally {
      setDetalhesLoading(false);
    }
  };

  const closeDetalhesModal = () => {
    setDetalhesModalVisible(false);
    setMatriculaDetalhes(null);
  };

  const closeParcelasModal = () => {
    setParcelasModalVisible(false);
    setParcelas([]);
    setMatriculaSelecionada(null);
  };

  const handleRecalcularParcelas = async () => {
    if (!matriculaSelecionada) return;
    setParcelasActionLoading(true);
    try {
      const response = await matriculaService.recalcularParcelas(matriculaSelecionada.id);
      const dados = response.data || {};
      message.success(
        `Parcelas recalculadas (${dados.total_criadas || 0} criada(s), ${dados.total_existentes || 0} já existiam).`
      );
      showSkippedPastParcelasModal(dados);
      await fetchParcelasDaMatricula(matriculaSelecionada.id);
      fetchMatriculas();
    } catch (error) {
      handleApiError(error, 'Erro ao recalcular parcelas');
    }
    setParcelasActionLoading(false);
  };

  const handleSincronizarParcelasComFaturas = async () => {
    if (!matriculaSelecionada) return;
    setParcelasActionLoading(true);
    try {
      const response = await matriculaService.sincronizarStatusParcelas(matriculaSelecionada.id);
      const dados = response?.data || {};
      message.success(
        `Sincronização concluída: ${dados.total_faturas_sincronizadas || 0} fatura(s), ${dados.total_parcelas_atualizadas || 0} parcela(s) atualizada(s), ${dados.total_sem_fatura || 0} parcela(s) sem fatura.`
      );
      await fetchParcelasDaMatricula(matriculaSelecionada.id);
      if (detalhesModalVisible && Number(matriculaDetalhes?.matricula?.id) === Number(matriculaSelecionada.id)) {
        const detalhesAtualizados = await matriculaService.getDetalhes(matriculaSelecionada.id);
        setMatriculaDetalhes(detalhesAtualizados?.data || null);
      }
      fetchMatriculas();
    } catch (error) {
      handleApiError(error, 'Erro ao sincronizar parcelas com faturas');
    }
    setParcelasActionLoading(false);
  };

  const handleBulkDeleteParcelas = () => {
    if (!matriculaSelecionada) return;
    Modal.confirm({
      title: 'Apagar todas as parcelas abertas?',
      content: 'A exclusão só será permitida se não houver fatura vinculada impeditiva para o mesmo aluno e competência.',
      okText: 'Apagar tudo',
      cancelText: 'Cancelar',
      okButtonProps: { danger: true },
      onOk: async () => {
        setParcelasActionLoading(true);
        try {
          const response = await matriculaService.deleteParcelasBulk(matriculaSelecionada.id);
          const removidas = response.data?.total_apagadas ?? 0;
          message.success(`${removidas} parcela(s) removida(s).`);
          showSkippedPastParcelasModal(response.data || {});
          await fetchParcelasDaMatricula(matriculaSelecionada.id);
          fetchMatriculas();
        } catch (error) {
          handleApiError(error, 'Erro ao apagar parcelas');
        } finally {
          setParcelasActionLoading(false);
        }
      },
    });
  };

  const handleDeleteParcela = async (parcelaId) => {
    if (!matriculaSelecionada) return;
    setParcelasActionLoading(true);
    try {
      await parcelaService.delete(parcelaId);
      message.success('Parcela removida com sucesso');
      await fetchParcelasDaMatricula(matriculaSelecionada.id);
      fetchMatriculas();
    } catch (error) {
      handleApiError(error, 'Erro ao remover parcela');
    } finally {
      setParcelasActionLoading(false);
    }
  };

  const confirmDeleteParcela = (parcela) => {
    Modal.confirm({
      title: `Excluir parcela ${parcela.numero_parcela}?`,
      content: 'A exclusão só será permitida se não houver fatura vinculada impeditiva para o mesmo aluno e competência.',
      okText: 'Excluir parcela',
      cancelText: 'Cancelar',
      okButtonProps: { danger: true },
      onOk: () => handleDeleteParcela(parcela.id),
    });
  };

  const getAlunoName = (id_alunos) => {
    const aluno = alunos.find((a) => a.id === id_alunos);
    return aluno ? aluno.nome_aluno : '-';
  };

  const getUnidadeName = (id_unidades) => {
    const unidade = unidades.find((u) => u.id === id_unidades);
    return unidade ? unidade.nome_fantasia : '-';
  };

  const getTurmaName = (id_turma) => {
    const turma = turmas.find((t) => t.id === id_turma);
    return turma ? turma.nome_turm : '-';
  };

  const getSerieName = (id_serie) => {
    const serie = series.find((s) => s.id === id_serie);
    return serie ? serie.nome_serie : '-';
  };

  const getSeriesPorUnidade = (id_unidades) => {
    if (!id_unidades) return [];
    return series.filter((s) => s.id_unidades === id_unidades);
  };

  const getContratoPrincipalAtivo = (matriculaId) => contratosPrincipaisAtivos[matriculaId] || null;

  const getMotivoGeracaoContratoBloqueada = (record) => {
    const contratoAtivo = getContratoPrincipalAtivo(record?.id);
    if (contratoAtivo) {
      return `Já existe contrato principal ativo (ID ${contratoAtivo.id}, status ${contratoAtivo.status_interno || '-'}) para esta matrícula.`;
    }

    return null;
  };

  const fetchMatriculas = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.current,
        page_size: pagination.pageSize,
      };

      if (filtrosAplicados.id) params.id = filtrosAplicados.id;
      if (filtrosAplicados.ano_letivo) params.ano_letivo = filtrosAplicados.ano_letivo;
      if (filtrosAplicados.id_alunos) params.id_alunos = filtrosAplicados.id_alunos;
      if (filtrosAplicados.id_unidades) params.id_unidades = filtrosAplicados.id_unidades;
      if (filtrosAplicados.id_turma) params.id_turma = filtrosAplicados.id_turma;
      if (filtrosAplicados.status) params.status = filtrosAplicados.status;
      if (filtrosAplicados.busca_geral?.trim()) params.busca_geral = filtrosAplicados.busca_geral.trim();

      const response = await matriculaService.listAdmin(params);
      const payload = response?.data || {};
      const matriculasData = Array.isArray(payload?.items)
        ? payload.items
        : (Array.isArray(payload) ? payload : []);
      const apiPagination = payload?.pagination || {};

      setMatriculas(matriculasData);
      setMatriculasFiltradas(matriculasData);
      setPagination((prev) => ({
        ...prev,
        current: apiPagination.page || prev.current,
        pageSize: apiPagination.page_size || prev.pageSize,
        total: typeof apiPagination.total === 'number' ? apiPagination.total : matriculasData.length,
      }));
      await fetchContratosPrincipaisAtivos(matriculasData);
    } catch (error) {
      message.error('Erro ao carregar matrículas');
      console.error(error);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchMatriculas();
  }, [pagination.current, pagination.pageSize, filtrosAplicados]);

  const fetchContratosPrincipaisAtivos = async (matriculasData) => {
    try {
      const matriculaIds = new Set((matriculasData || []).map((m) => m.id));
      if (!matriculaIds.size) {
        setContratosPrincipaisAtivos({});
        return;
      }

      const response = await contratoService.list({ tipo_documento: 'PRINCIPAL' });
      const contratos = Array.isArray(response.data) ? response.data : [];

      const ativosPorMatricula = {};
      contratos.forEach((contrato) => {
        const matriculaId = contrato?.id_matricula;
        const statusInterno = (contrato?.status_interno || '').toUpperCase();
        if (
          matriculaIds.has(matriculaId)
          && STATUS_CONTRATO_PRINCIPAL_ATIVO.includes(statusInterno)
          && !ativosPorMatricula[matriculaId]
        ) {
          ativosPorMatricula[matriculaId] = contrato;
        }
      });

      setContratosPrincipaisAtivos(ativosPorMatricula);
    } catch (error) {
      console.error('Erro ao carregar contratos ativos por matrícula', error);
    }
  };

  const handleAbrirModalGerarContrato = async (record) => {
    const motivoBloqueio = getMotivoGeracaoContratoBloqueada(record);
    if (motivoBloqueio) {
      message.warning(motivoBloqueio);
      return;
    }

    setMatriculaContratoSelecionada(record);
    setContratoTemplateSelecionado(CONTRATO_TEMPLATE_OPTIONS[0].value);
    setContratoPreviewModalVisible(true);
    await carregarPreviewContrato(record.id);
  };

  const handleContinuarGeracaoContrato = () => {
    if (contratoPreviewLoading || contratoPreviewError || !contratoPreviewData) {
      return;
    }
    setContratoPreviewModalVisible(false);
    setContratoModalVisible(true);
  };

  const handleGerarContrato = async () => {
    if (!matriculaContratoSelecionada?.id) return;
    if (!contratoTemplateSelecionado) {
      message.warning('Selecione um template para gerar o contrato.');
      return;
    }

    setGerarContratoLoading(true);
    try {
      await contratoService.gerarPorMatricula(matriculaContratoSelecionada.id, {
        template_id: contratoTemplateSelecionado,
      });
      message.success('Contrato gerado com sucesso.');
      setContratoModalVisible(false);
      setContratoPreviewModalVisible(false);
      setMatriculaContratoSelecionada(null);
      setContratoPreviewData(null);
      setContratoPreviewError(null);
      await fetchMatriculas();
    } catch (error) {
      handleApiError(error, 'Erro ao gerar contrato');
    } finally {
      setGerarContratoLoading(false);
    }
  };

  const fetchAlunos = async () => {
    try {
      const allAlunos = await fetchAllPaginated(alunoService.list);
      setAlunos(allAlunos);
    } catch (error) {
      console.error('Erro ao carregar alunos');
    }
  };

  const fetchUnidades = async () => {
    try {
      const response = await unidadeService.list();
      setUnidades(response.data);
    } catch (error) {
      console.error('Erro ao carregar unidades');
    }
  };

  const fetchTurmas = async () => {
    try {
      const response = await turmaService.list();
      setTurmas(response.data);
    } catch (error) {
      console.error('Erro ao carregar turmas');
    }
  };

  const fetchSeries = async () => {
    try {
      const response = await serieService.list();
      setSeries(response.data);
    } catch (error) {
      console.error('Erro ao carregar séries');
    }
  };

  const handleAdd = () => {
    form.resetFields();
    const defaultStatus = user?.role === 'RESPONSAVEL' ? 'PENDENTE' : 'ATIVA';
    form.setFieldsValue({
      status: defaultStatus,
      tipo_matricula: 'ANUAL',
      ano_letivo: String(dayjs().year()),
    });
    setEditingId(null);
    setStatusOriginalEdicao(null);
    setIsModalVisible(true);
  };

  const handleEdit = async (record) => {
    setLoading(true);
    try {
      const response = await matriculaService.get(record.id);
      const detalhe = response?.data;
      if (!detalhe) {
        message.error('Não foi possível carregar os dados da matrícula');
        return;
      }
      setEditingId(detalhe.id);
      setStatusOriginalEdicao((detalhe.status || '').toUpperCase() || null);
      const formData = {
        ...detalhe,
        data_inicio: detalhe.data_inicio ? dayjs(detalhe.data_inicio) : null,
        data_fim: detalhe.data_fim ? dayjs(detalhe.data_fim) : null,
      };
      form.setFieldsValue(formData);
      setIsModalVisible(true);
    } catch (error) {
      handleApiError(error, 'Erro ao carregar matrícula para edição');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    Modal.confirm({
      title: 'Confirmação',
      content: 'Deseja excluir esta matrícula?',
      okText: 'Sim',
      cancelText: 'Não',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await matriculaService.delete(id);
          message.success('Matrícula excluída com sucesso');
          fetchMatriculas();
        } catch (error) {
          message.error('Erro ao excluir matrícula');
        }
      },
    });
  };

  const abrirModalTrocaVigencia = (record) => {
    const isMensalidade = ((record?.tipo_matricula || 'ANUAL').toUpperCase() === 'MENSALIDADE');
    setMatriculaTrocaSelecionada(record);
    formTrocaVigencia.setFieldsValue({
      id_turma: record?.id_turma ?? null,
      valor_total: record?.valor_total ?? null,
      valor_mensal: record?.valor_mensal ?? null,
      qtd_parcelas_restantes: record?.qtd_parcelas ?? null,
      desconto_percentual: record?.desconto ?? 0,
      data_inicio: dayjs(),
      data_fim: isMensalidade
        ? (record?.data_fim ? dayjs(record.data_fim) : null)
        : null,
    });
    setTrocaVigenciaModalVisible(true);
  };

  const fecharModalTrocaVigencia = () => {
    setTrocaVigenciaModalVisible(false);
    setMatriculaTrocaSelecionada(null);
    formTrocaVigencia.resetFields();
  };

  const abrirModalAlterarVencimento = (record = null) => {
    const matriculaAlvo = record || (editingId
      ? {
          id: editingId,
          dia_vencimento: form.getFieldValue('dia_vencimento') ?? null,
        }
      : null);

    if (!matriculaAlvo?.id) return;

    setMatriculaVencimentoSelecionada(matriculaAlvo);
    formAlterarVencimento.setFieldsValue({
      novo_dia_vencimento: matriculaAlvo?.dia_vencimento ?? null,
      data_inicio_aplicacao: dayjs(),
    });
    setAlterarVencimentoModalVisible(true);
  };

  const fecharModalAlterarVencimento = () => {
    setAlterarVencimentoModalVisible(false);
    setMatriculaVencimentoSelecionada(null);
    formAlterarVencimento.resetFields();
  };

  const handleAlterarVencimentoSubmit = async (values) => {
    if (!matriculaVencimentoSelecionada?.id) return;

    setAlterarVencimentoLoading(true);
    try {
      const payload = {
        novo_dia_vencimento: values.novo_dia_vencimento,
        data_inicio_aplicacao: values.data_inicio_aplicacao
          ? values.data_inicio_aplicacao.format('YYYY-MM-DD')
          : null,
      };

      const response = await matriculaService.alterarVencimentoFuturo(
        matriculaVencimentoSelecionada.id,
        payload,
      );

      const data = response?.data || {};
      const novoDiaSalvo = data?.dia_vencimento_atual || payload.novo_dia_vencimento;
      const totalAlteradas = data?.parcelas_regeneradas || 0;
      if ((data.parcelas_regeneradas || 0) === 0 && data.user_message) {
        message.info(`${data.user_message} Dia atual salvo: ${novoDiaSalvo}.`);
      } else {
        message.success(
          `Novo vencimento salvo: dia ${novoDiaSalvo}. ${totalAlteradas} parcela(s) futura(s) alterada(s).`
        );
      }

      if (editingId && Number(matriculaVencimentoSelecionada.id) === Number(editingId)) {
        form.setFieldsValue({ dia_vencimento: novoDiaSalvo });
      }

      fecharModalAlterarVencimento();
      fetchMatriculas();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      const fallback = 'Não foi possível alterar vencimento futuro da matrícula.';

      if (typeof detail === 'string' && detail.trim()) {
        message.error(detail.trim());
      } else if (detail && typeof detail === 'object' && detail.user_message) {
        message.error(detail.user_message);
      } else {
        message.error(fallback);
      }
    } finally {
      setAlterarVencimentoLoading(false);
    }
  };

  const handleTrocaVigenciaSubmit = async (values) => {
    if (!matriculaTrocaSelecionada?.id) return;

    setTrocaVigenciaLoading(true);
    try {
      const payloadBase = {
        id_turma: values.id_turma,
        desconto_percentual: values.desconto_percentual ?? 0,
        data_inicio: values.data_inicio ? values.data_inicio.format('YYYY-MM-DD') : null,
      };

      const isMensalidade = ((matriculaTrocaSelecionada?.tipo_matricula || 'ANUAL').toUpperCase() === 'MENSALIDADE');
      const payload = isMensalidade
        ? {
            ...payloadBase,
            valor_mensal: values.valor_mensal,
            data_fim: values.data_fim ? values.data_fim.format('YYYY-MM-DD') : null,
          }
        : {
            ...payloadBase,
            valor_total: values.valor_total,
            qtd_parcelas_restantes: values.qtd_parcelas_restantes,
          };

      await matriculaService.trocarVigencia(matriculaTrocaSelecionada.id, payload);
      message.success('Troca de vigência realizada com sucesso');
      fecharModalTrocaVigencia();
      fetchMatriculas();
    } catch (error) {
      const statusCode = error?.response?.status;
      const detail = error?.response?.data?.detail;

      if (statusCode === 409) {
        const conflitoMsg =
          (typeof detail === 'string' && detail) ||
          (detail && typeof detail === 'object' && detail.user_message) ||
          'Conflito de vigência. Revise a data de início e vigências existentes.';
        message.error(conflitoMsg);
      } else {
        handleApiError(error, 'Erro ao trocar vigência da matrícula');
      }
    } finally {
      setTrocaVigenciaLoading(false);
    }
  };

  const extrairMensagemErro = (error, fallback) => {
    const detail = error?.response?.data?.detail;
    if (typeof detail === 'string' && detail.trim()) {
      return detail.trim();
    }
    if (detail && typeof detail === 'object') {
      if (typeof detail.user_message === 'string' && detail.user_message.trim()) {
        return detail.user_message.trim();
      }
      if (typeof detail.message === 'string' && detail.message.trim()) {
        return detail.message.trim();
      }
    }
    return fallback;
  };

  const abrirModalCorrigirVigenciaInicial = async (record) => {
    if (!record?.id) return;

    setMatriculaCorrecaoSelecionada(record);
    setCorrigirVigenciaInicialModalVisible(true);
    setCorrigirVigenciaInicialLoading(true);
    formCorrigirVigenciaInicial.resetFields();

    try {
      const [resMatricula, resDetalhes] = await Promise.all([
        matriculaService.get(record.id),
        matriculaService.getDetalhes(record.id),
      ]);

      const matriculaData = resMatricula?.data || {};
      const vigencias = Array.isArray(resDetalhes?.data?.vigencias) ? resDetalhes.data.vigencias : [];

      const vigenciaInicial = [...vigencias]
        .filter((vigencia) => vigencia?.origem === 'persistida' && vigencia?.data_inicio)
        .sort((a, b) => {
          const dataA = dayjs(a.data_inicio).valueOf();
          const dataB = dayjs(b.data_inicio).valueOf();
          if (dataA !== dataB) return dataA - dataB;
          return Number(a?.id || 0) - Number(b?.id || 0);
        })[0];

      formCorrigirVigenciaInicial.setFieldsValue({
        data_inicio: vigenciaInicial?.data_inicio ? dayjs(vigenciaInicial.data_inicio) : (matriculaData?.data_inicio ? dayjs(matriculaData.data_inicio) : null),
        data_fim: vigenciaInicial?.data_fim ? dayjs(vigenciaInicial.data_fim) : (matriculaData?.data_fim ? dayjs(matriculaData.data_fim) : null),
        id_turma: vigenciaInicial?.turma?.id ?? matriculaData?.id_turma ?? null,
        valor_total: vigenciaInicial?.valor_total ?? matriculaData?.valor_total ?? null,
        desconto_percentual: (matriculaData?.desconto ?? 0),
      });
    } catch (error) {
      const msg = extrairMensagemErro(error, 'Erro ao carregar dados da vigência inicial para correção.');
      message.error(msg);
      setCorrigirVigenciaInicialModalVisible(false);
      setMatriculaCorrecaoSelecionada(null);
      formCorrigirVigenciaInicial.resetFields();
    } finally {
      setCorrigirVigenciaInicialLoading(false);
    }
  };

  const fecharModalCorrigirVigenciaInicial = () => {
    if (corrigirVigenciaInicialLoading) return;
    setCorrigirVigenciaInicialModalVisible(false);
    setMatriculaCorrecaoSelecionada(null);
    formCorrigirVigenciaInicial.resetFields();
  };

  const handleCorrigirVigenciaInicialSubmit = async (values) => {
    if (!matriculaCorrecaoSelecionada?.id) return;

    setCorrigirVigenciaInicialLoading(true);
    try {
      const payload = {
        data_inicio: values.data_inicio ? values.data_inicio.format('YYYY-MM-DD') : null,
        data_fim: values.data_fim ? values.data_fim.format('YYYY-MM-DD') : null,
      };

      if (values.id_turma) payload.id_turma = values.id_turma;
      if (values.valor_total !== undefined && values.valor_total !== null) payload.valor_total = values.valor_total;
      if (values.desconto_percentual !== undefined && values.desconto_percentual !== null) payload.desconto_percentual = values.desconto_percentual;

      await matriculaService.corrigirVigenciaInicial(matriculaCorrecaoSelecionada.id, payload);
      message.success('Vigência inicial corrigida com sucesso.');

      if (detalhesModalVisible && Number(matriculaDetalhes?.matricula?.id) === Number(matriculaCorrecaoSelecionada.id)) {
        const detalhesAtualizados = await matriculaService.getDetalhes(matriculaCorrecaoSelecionada.id);
        setMatriculaDetalhes(detalhesAtualizados?.data || null);
      }

      if (parcelasModalVisible && Number(matriculaSelecionada?.id) === Number(matriculaCorrecaoSelecionada.id)) {
        await fetchParcelasDaMatricula(matriculaCorrecaoSelecionada.id);
      }

      fecharModalCorrigirVigenciaInicial();
      fetchMatriculas();
    } catch (error) {
      const msg = extrairMensagemErro(error, 'Não foi possível corrigir a vigência inicial.');
      message.error(msg);
    } finally {
      setCorrigirVigenciaInicialLoading(false);
    }
  };

  const abrirCorrecaoVigenciaInicialNaEdicao = () => {
    if (!editingId) return;

    abrirModalCorrigirVigenciaInicial({
      id: editingId,
      id_unidades: form.getFieldValue('id_unidades'),
      id_turma: form.getFieldValue('id_turma'),
      valor_total: form.getFieldValue('valor_total'),
      desconto: form.getFieldValue('desconto'),
    });
  };

  const handleValuesChange = (changedValues, allValues) => {
    if ('id_alunos' in changedValues && !editingId) {
      const aluno = alunos.find((a) => a.id === changedValues.id_alunos);
      const idUnidadeAluno = aluno?.id_unidades || null;
      form.setFieldsValue({ id_unidades: idUnidadeAluno, id_turma: null });
    }

    if ('id_unidades' in changedValues) {
      form.setFieldsValue({ id_turma: null, id_serie: null });
    }

    const tipoMatricula = (allValues.tipo_matricula || 'ANUAL').toUpperCase();

    if ('tipo_matricula' in changedValues) {
      if (tipoMatricula === 'MENSALIDADE') {
        form.setFieldsValue({ valor_total: null });
      } else {
        form.setFieldsValue({ valor_mensal: null });
      }
    }

    if ('valor_total' in changedValues || 'valor_mensal' in changedValues || 'qtd_parcelas' in changedValues || 'tipo_matricula' in changedValues) {
      let valorParcela = null;
      if (tipoMatricula === 'MENSALIDADE') {
        if (allValues.valor_mensal !== undefined && allValues.valor_mensal !== null) {
          valorParcela = allValues.valor_mensal;
        }
      } else if (allValues.valor_total && allValues.qtd_parcelas && allValues.qtd_parcelas > 0) {
        valorParcela = allValues.valor_total / allValues.qtd_parcelas;
      }

      if (valorParcela !== null) {
        form.setFieldsValue({ valor_parcela: valorParcela });
        const descontoPercentual = allValues.desconto || 0;
        const parcelaFinal = valorParcela - (valorParcela * descontoPercentual / 100);
        form.setFieldsValue({ parcela_final: parcelaFinal });
      }
    }

    if ('desconto' in changedValues) {
      const valorParcela = allValues.valor_parcela;
      if (valorParcela !== undefined && valorParcela !== null) {
        const descontoPercentual = allValues.desconto || 0;
        const parcelaFinal = valorParcela - (valorParcela * descontoPercentual / 100);
        form.setFieldsValue({ parcela_final: parcelaFinal });
      }
    }
  };

  const handleSubmit = async (values) => {
    try {
      const statusSelecionado = (values.status || '').toUpperCase();
      const deveCancelarViaFluxoNovo = editingId
        && statusSelecionado === 'CANCELADA'
        && statusOriginalEdicao !== 'CANCELADA';

      if (deveCancelarViaFluxoNovo) {
        Modal.confirm({
          title: 'Confirmar cancelamento da matrícula?',
          okText: 'Confirmar cancelamento',
          cancelText: 'Voltar',
          okButtonProps: { danger: true },
          content: (
            <div>
              <Typography.Paragraph style={{ marginBottom: 8 }}>
                Esta ação irá cancelar a matrícula e encerrar a vigência ativa.
              </Typography.Paragraph>
              <Typography.Paragraph style={{ marginBottom: 8 }}>
                Parcelas futuras em aberto serão removidas/canceladas.
              </Typography.Paragraph>
              <Typography.Paragraph style={{ marginBottom: 0 }}>
                Parcelas já pagas serão preservadas para manter o histórico financeiro.
              </Typography.Paragraph>
            </div>
          ),
          onOk: async () => {
            try {
              const response = await matriculaService.cancelar(editingId);
              const totalCanceladas = response?.data?.parcelas_futuras_canceladas ?? 0;
              message.success(`Matrícula cancelada com sucesso (${totalCanceladas} parcela(s) futura(s) cancelada(s)).`);
              setIsModalVisible(false);
              setStatusOriginalEdicao('CANCELADA');
              fetchMatriculas();
            } catch (error) {
              handleApiError(error, 'Não foi possível cancelar a matrícula.');
              throw error;
            }
          },
        });
        return;
      }

      const submitData = {
        ...values,
        data_inicio: values.data_inicio ? values.data_inicio.format('YYYY-MM-DD') : null,
        data_fim: values.data_fim ? values.data_fim.format('YYYY-MM-DD') : null,
      };

      const tipoMatriculaSubmit = (submitData.tipo_matricula || 'ANUAL').toUpperCase();
      if (tipoMatriculaSubmit === 'MENSALIDADE') {
        if (submitData.valor_mensal !== undefined && submitData.valor_mensal !== null) {
          submitData.valor_parcela = submitData.valor_mensal;
        }
      } else if (submitData.valor_total && submitData.qtd_parcelas && submitData.qtd_parcelas > 0) {
        submitData.valor_parcela = submitData.valor_total / submitData.qtd_parcelas;
      }

      // Calcular parcela_final automaticamente (com desconto percentual)
      if (submitData.valor_parcela !== undefined) {
        const descontoPercentual = submitData.desconto || 0;
        submitData.parcela_final = submitData.valor_parcela - (submitData.valor_parcela * descontoPercentual / 100);
      }

      let response;
      if (editingId) {
        try {
          const ativandoPendente =
            statusOriginalEdicao === 'PENDENTE' && statusSelecionado === 'ATIVA';

          if (ativandoPendente) {
            response = await matriculaService.ativar(editingId, submitData);
            message.success('Matrícula ativada com sucesso');
          } else {
            response = await matriculaService.update(editingId, submitData);
            message.success('Matrícula atualizada com sucesso');
          }
        } catch (error) {
          const statusCode = error?.response?.status;
          const detail = error?.response?.data?.detail;

          if (
            statusCode === 409
            && detail
            && typeof detail === 'object'
            && detail.error_code === 'VIGENCIA_FIELD_UPDATE_BLOCKED'
          ) {
            Modal.warning({
              title: 'Alteração de turma/valores agora é por vigência',
              content: (
                <div>
                  <Typography.Paragraph style={{ marginBottom: 8 }}>
                    {detail.user_message || 'Use a ação de troca de vigência para turma e valores.'}
                  </Typography.Paragraph>
                  <Typography.Text type="secondary">
                    Dica: use o botão "Alterar turma" na linha da matrícula.
                  </Typography.Text>
                </div>
              ),
            });
            return;
          }

          throw error;
        }
      } else {
        response = await matriculaService.create(submitData);
        message.success('Matrícula criada com sucesso');
      }
      showSkippedPastParcelasModal(response?.data || {});
      setIsModalVisible(false);
      setStatusOriginalEdicao(null);
      fetchMatriculas();
    } catch (error) {
      handleApiError(error, 'Erro ao salvar matrícula');
      console.error(error);
    }
  };

  const handleFiltroChange = (campo, valor) => {
    setFiltros(prev => ({
      ...prev,
      [campo]: valor
    }));
  };

  const aplicarFiltros = () => {
    setFiltrosAplicados({ ...filtros });
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const limparFiltros = () => {
    const reset = {
      id: '',
      ano_letivo: null,
      id_alunos: null,
      id_unidades: null,
      id_turma: null,
      status: null,
      busca_geral: '',
    };
    setFiltros(reset);
    setFiltrosAplicados(reset);
    setPagination((prev) => ({ ...prev, current: 1 }));
    message.success('Filtros limpos');
  };

  const onTableChange = (newPagination) => {
    setPagination((prev) => ({
      ...prev,
      current: newPagination?.current || 1,
      pageSize: newPagination?.pageSize || prev.pageSize,
    }));
  };

  const getAnosLetivos = () => {
    const anos = [...new Set(matriculas.map(m => m.ano_letivo).filter(Boolean))];
    return anos.sort((a, b) => b - a);
  };

  const getTurmasPorUnidade = (id_unidades) => {
    if (!id_unidades) return [];
    return turmas.filter((t) => t.id_unidades === id_unidades);
  };

  const totalPendentes = matriculas.filter((m) => (m.status || '').toUpperCase() === 'PENDENTE').length;

  const totalParcelasGeradas = parcelas.length;
  const totalParcelasEsperadas = matriculaSelecionada?.qtd_parcelas || 0;
  const parcelasPendentes = Math.max(totalParcelasEsperadas - totalParcelasGeradas, 0);
  const descontoModalValor = Number(
    matriculaSelecionada?.desconto ?? (parcelas[0]?.desconto_percentual ?? 0)
  );
  const hasDescontoModal = Number.isFinite(descontoModalValor) && descontoModalValor > 0;

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 50,
      render: (id) => <ShortId id={id} />,
      sorter: (a, b) => (Number(a?.id) || 0) - (Number(b?.id) || 0),
    },
    {
      title: 'Ano Letivo',
      dataIndex: 'ano_letivo',
      key: 'ano_letivo',
      width: 80,
    },
    {
      title: 'Aluno',
      dataIndex: 'id_alunos',
      key: 'id_alunos',
      render: (id_alunos) => getAlunoName(id_alunos),
      sorter: (a, b) => getAlunoName(a.id_alunos).localeCompare(getAlunoName(b.id_alunos)),
    },
    {
      title: 'Unidade',
      dataIndex: 'id_unidades',
      key: 'id_unidades',
      render: (id_unidades) => getUnidadeName(id_unidades),
      sorter: (a, b) => getUnidadeName(a.id_unidades).localeCompare(getUnidadeName(b.id_unidades)),
    },
    {
      title: 'Contrato',
      key: 'status_contrato',
      width: 160,
      render: (_, record) => {
        const contratoAtivo = getContratoPrincipalAtivo(record.id);
        if (!contratoAtivo) {
          return <Tag color="default">Sem contrato ativo</Tag>;
        }
        return (
          <Tag color="blue">
            Ativo #{contratoAtivo.id}
          </Tag>
        );
      },
    },
    {
      title: 'Turma',
      dataIndex: 'id_turma',
      key: 'id_turma',
      render: (id_turma) => getTurmaName(id_turma),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (status) => {
        const value = (status || 'PENDENTE').toUpperCase();
        const color = value === 'ATIVA' ? 'green' : value === 'CANCELADA' ? 'red' : 'orange';
        return <Tag color={color}>{value}</Tag>;
      },
    },
    {
      title: 'Valor Total',
      dataIndex: 'valor_total',
      key: 'valor_total',
      render: (valor) => (valor ? `R$ ${valor.toFixed(2)}` : '-'),
      width: 100,
    },
    {
      title: 'Ações',
      key: 'actions',
      width: 320,
      render: (_, record) => {
        if (user?.role === 'RESPONSAVEL') return null;

        const motivoBloqueioContrato = getMotivoGeracaoContratoBloqueada(record);
        const podeGerarContrato = !motivoBloqueioContrato;

        return (
          <Space>
            <Tooltip title="Gerenciar parcelas da matrícula">
              <Button
                icon={<EyeOutlined />}
                onClick={() => openParcelasModal(record)}
                size="small"
              >
                Parcelas
              </Button>
            </Tooltip>
            <Tooltip title="Visualizar dados atuais, histórico de vigências e parcelas">
              <Button
                icon={<EyeOutlined />}
                onClick={() => openDetalhesModal(record)}
                size="small"
              >
                Ver detalhes
              </Button>
            </Tooltip>
            <Tooltip title={motivoBloqueioContrato || 'Gerar contrato principal'}>
              <span>
                <Button
                  size="small"
                  disabled={!podeGerarContrato}
                  onClick={() => handleAbrirModalGerarContrato(record)}
                >
                  Gerar Contrato
                </Button>
              </span>
            </Tooltip>
            <Tooltip title="Trocar turma/valores por vigência">
              <Button
                icon={<SwapOutlined />}
                onClick={() => abrirModalTrocaVigencia(record)}
                size="small"
              >
                Alterar turma
              </Button>
            </Tooltip>
            <Button
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
              size="small"
            />
            {user?.role !== 'OFFICE' && (
              <Button
                icon={<DeleteOutlined />}
                danger
                onClick={() => handleDelete(record.id)}
                size="small"
              />
            )}
          </Space>
        );
      },
    },
  ];

  const parcelaColumns = [
    {
      title: '#',
      dataIndex: 'numero_parcela',
      key: 'numero_parcela',
      width: 70,
    },
    {
      title: 'Competência',
      dataIndex: 'competencia',
      key: 'competencia',
      width: 110,
    },
    {
      title: 'Vencimento',
      dataIndex: 'data_vencimento',
      key: 'data_vencimento',
      render: (date) => formatDate(date),
      width: 130,
    },
    {
      title: 'Valor',
      dataIndex: 'valor',
      key: 'valor',
      render: (valor) => formatCurrency(valor),
      width: 110,
    },
    {
      title: 'Valor com desconto',
      key: 'valor_com_desconto',
      render: (_, record) => {
        const desconto = Number(record.desconto_percentual || 0);
        if (!Number.isFinite(desconto) || desconto <= 0) {
          return formatCurrency(record.valor);
        }
        return formatCurrency(record.valor_com_desconto);
      },
      width: 150,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const value = status || 'ABERTA';
        const palette = value === 'PAGA' ? 'green' : value === 'CANCELADA' ? 'red' : 'blue';
        return <Tag color={palette}>{value}</Tag>;
      },
    },
    {
      title: 'Ações',
      key: 'parcelas_actions',
      width: 120,
      render: (_, parcela) => {
        const status = (parcela.status || '').toUpperCase();
        const hasFatura = !!parcela.id_fatura;
        const podeExcluirParcela = ((status === 'ABERTA' || status === 'CANCELADA') && !hasFatura);
        return (
          <Tooltip
            title={
              podeExcluirParcela
                ? 'Apagar parcela'
                : 'Apenas parcelas ABERTAS ou CANCELADAS sem fatura podem ser apagadas'
            }
          >
            <Button
              icon={<DeleteOutlined />}
              size="small"
              danger
              disabled={!podeExcluirParcela || parcelasActionLoading}
              onClick={() => confirmDeleteParcela(parcela)}
            />
          </Tooltip>
        );
      },
    },
  ];

  const vigenciasDetalheColumns = [
    {
      title: 'Início',
      dataIndex: 'data_inicio',
      key: 'data_inicio',
      render: (value) => formatDate(value),
      width: 120,
    },
    {
      title: 'Fim',
      dataIndex: 'data_fim',
      key: 'data_fim',
      render: (value) => formatDate(value),
      width: 120,
    },
    {
      title: 'Turma',
      key: 'turma',
      render: (_, record) => record?.turma?.nome || '-',
    },
    {
      title: 'Valor Total',
      dataIndex: 'valor_total',
      key: 'valor_total',
      align: 'right',
      render: (value) => formatCurrency(value),
      width: 130,
    },
    {
      title: 'Valor Parcela',
      dataIndex: 'valor_parcela',
      key: 'valor_parcela',
      align: 'right',
      render: (value) => formatCurrency(value),
      width: 130,
    },
    {
      title: 'Parcela Final',
      dataIndex: 'parcela_final',
      key: 'parcela_final',
      align: 'right',
      render: (value) => formatCurrency(value),
      width: 130,
    },
  ];

  const parcelasDetalheColumns = [
    {
      title: 'Parcela',
      dataIndex: 'numero_parcela',
      key: 'numero_parcela',
      width: 90,
    },
    {
      title: 'Competência',
      dataIndex: 'competencia',
      key: 'competencia',
      width: 110,
    },
    {
      title: 'Vencimento',
      dataIndex: 'data_vencimento',
      key: 'data_vencimento',
      render: (value) => formatDate(value),
      width: 120,
    },
    {
      title: 'Valor',
      dataIndex: 'valor',
      key: 'valor',
      align: 'right',
      render: (value) => formatCurrency(value),
      width: 120,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (status) => {
        const value = status || 'ABERTA';
        const palette = value === 'PAGA' ? 'green' : value === 'CANCELADA' ? 'red' : 'blue';
        return <Tag color={palette}>{value}</Tag>;
      },
    },
    {
      title: 'ID Fatura',
      dataIndex: 'id_fatura',
      key: 'id_fatura',
      width: 100,
      render: (value) => value ?? '-',
    },
  ];

  return (
    <div>
      {/* Painel de Filtros */}
      <Card 
        title={
          <Space>
            <FilterOutlined />
            Filtros de Busca
            <span style={{ fontSize: '12px', fontWeight: 'normal', color: '#888' }}>
              ({matriculas.length} de {pagination.total} matrículas)
            </span>
          </Space>
        }
        extra={
          <Button 
            type="text" 
            size="small" 
            onClick={() => setMostrarFiltros(!mostrarFiltros)}
          >
            {mostrarFiltros ? 'Ocultar' : 'Mostrar'}
          </Button>
        }
        style={{ marginBottom: 16 }}
      >
        {mostrarFiltros && (
          <div>
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={8} lg={6}>
                <div style={{ marginBottom: 4, fontWeight: 500 }}>ID</div>
                <Input
                  placeholder="Filtrar por ID"
                  value={filtros.id}
                  onChange={(e) => handleFiltroChange('id', String(e?.target?.value || '').replace(/\D/g, ''))}
                  onPressEnter={aplicarFiltros}
                  allowClear
                />
              </Col>
              <Col xs={24} sm={12} md={8} lg={6}>
                <div style={{ marginBottom: 4, fontWeight: 500 }}>Busca Geral</div>
                <Input
                  placeholder="Pesquisar por aluno, unidade, turma..."
                  prefix={<SearchOutlined />}
                  value={filtros.busca_geral}
                  onChange={(e) => handleFiltroChange('busca_geral', e.target.value)}
                  onPressEnter={aplicarFiltros}
                  allowClear
                />
              </Col>

              <Col xs={24} sm={12} md={8} lg={6}>
                <div style={{ marginBottom: 4, fontWeight: 500 }}>Ano Letivo</div>
                <Select
                  placeholder="Todos os anos"
                  value={filtros.ano_letivo}
                  onChange={(value) => handleFiltroChange('ano_letivo', value)}
                  allowClear
                  style={{ width: '100%' }}
                >
                  {getAnosLetivos().map(ano => (
                    <Select.Option key={ano} value={ano}>{ano}</Select.Option>
                  ))}
                </Select>
              </Col>

              <Col xs={24} sm={12} md={8} lg={6}>
                <div style={{ marginBottom: 4, fontWeight: 500 }}>Aluno</div>
                <Select
                  placeholder="Todos os alunos"
                  value={filtros.id_alunos}
                  onChange={(value) => handleFiltroChange('id_alunos', value)}
                  allowClear
                  showSearch
                  optionFilterProp="children"
                  style={{ width: '100%' }}
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {alunos.map(aluno => (
                    <Select.Option key={aluno.id} value={aluno.id}>
                      {aluno.nome_aluno}
                    </Select.Option>
                  ))}
                </Select>
              </Col>

              <Col xs={24} sm={12} md={8} lg={6}>
                <div style={{ marginBottom: 4, fontWeight: 500 }}>Unidade</div>
                <Select
                  placeholder="Todas as unidades"
                  value={filtros.id_unidades}
                  onChange={(value) => handleFiltroChange('id_unidades', value)}
                  allowClear
                  showSearch
                  optionFilterProp="children"
                  style={{ width: '100%' }}
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {unidades.map(unidade => (
                    <Select.Option key={unidade.id} value={unidade.id}>
                      {unidade.nome_fantasia}
                    </Select.Option>
                  ))}
                </Select>
              </Col>

              <Col xs={24} sm={12} md={8} lg={6}>
                <div style={{ marginBottom: 4, fontWeight: 500 }}>Turma</div>
                <Select
                  placeholder="Todas as turmas"
                  value={filtros.id_turma}
                  onChange={(value) => handleFiltroChange('id_turma', value)}
                  allowClear
                  showSearch
                  optionFilterProp="children"
                  style={{ width: '100%' }}
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {turmas.map(turma => (
                    <Select.Option key={turma.id} value={turma.id}>
                      {turma.nome_turm}
                    </Select.Option>
                  ))}
                </Select>
              </Col>

              <Col xs={24} sm={12} md={8} lg={6}>
                <div style={{ marginBottom: 4, fontWeight: 500 }}>Status</div>
                <Select
                  placeholder="Todos os status"
                  value={filtros.status}
                  onChange={(value) => handleFiltroChange('status', value)}
                  allowClear
                  style={{ width: '100%' }}
                >
                  {MATRICULA_STATUS_OPTIONS.map((option) => (
                    <Select.Option key={option.value} value={option.value}>
                      {option.label}
                    </Select.Option>
                  ))}
                </Select>
              </Col>

              <Col xs={24} sm={12} md={8} lg={6}>
                <div style={{ marginBottom: 4, fontWeight: 500 }}>&nbsp;</div>
                <Button
                  icon={<SearchOutlined />}
                  type="primary"
                  onClick={aplicarFiltros}
                  block
                  style={{ marginBottom: 8 }}
                >
                  Buscar
                </Button>
                <Button 
                  icon={<ClearOutlined />} 
                  onClick={limparFiltros}
                  block
                >
                  Limpar Filtros
                </Button>
              </Col>
            </Row>
          </div>
        )}
      </Card>

      {/* Botão Nova Matrícula */}
      {user?.role !== 'RESPONSAVEL' && (
        <div style={{ marginBottom: 16 }}>
          <Space>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
              Nova Matrícula
            </Button>
          </Space>
        </div>
      )}

      {totalPendentes > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={`Existem ${totalPendentes} matrícula(s) com status PENDENTE.`}
        />
      )}

      {/* Tabela */}
      <Table
        columns={columns}
        dataSource={matriculasFiltradas}
        loading={loading}
        rowKey="id"
        pagination={{ 
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showTotal: (total) => `Total de ${total} matrículas`,
          pageSizeOptions: ['10', '20', '50', '100']
        }}
        onChange={onTableChange}
        scroll={{ x: 1300, y: 520 }}
        sticky
      />

      <Modal
        title={`Preview do Contrato - Matrícula #${matriculaContratoSelecionada?.id || ''}`}
        open={contratoPreviewModalVisible}
        onCancel={() => {
          setContratoPreviewModalVisible(false);
          setMatriculaContratoSelecionada(null);
          setContratoPreviewData(null);
          setContratoPreviewError(null);
        }}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setContratoPreviewModalVisible(false);
              setMatriculaContratoSelecionada(null);
              setContratoPreviewData(null);
              setContratoPreviewError(null);
            }}
          >
            Cancelar
          </Button>,
          <Button
            key="continue"
            type="primary"
            onClick={handleContinuarGeracaoContrato}
            disabled={contratoPreviewLoading || !!contratoPreviewError || !contratoPreviewData}
          >
            Continuar
          </Button>,
        ]}
        width={760}
        destroyOnClose
      >
        {contratoPreviewLoading && (
          <Typography.Text>Carregando preview do contrato...</Typography.Text>
        )}

        {!contratoPreviewLoading && contratoPreviewError && (
          <Alert
            type="error"
            showIcon
            message={contratoPreviewError.message}
            description={
              contratoPreviewError.missingFields?.length ? (
                <div>
                  Campos faltantes: {contratoPreviewError.missingFields.join(', ')}
                </div>
              ) : null
            }
          />
        )}

        {!contratoPreviewLoading && !contratoPreviewError && contratoPreviewData && (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Card size="small" title="Aluno">
              <Descriptions size="small" column={1}>
                <Descriptions.Item label="Aluno">{contratoPreviewData.nome_aluno || '-'}</Descriptions.Item>
                <Descriptions.Item label="Data de nascimento">{formatDate(contratoPreviewData.data_nascimento_aluno)}</Descriptions.Item>
              </Descriptions>
            </Card>

            <Card size="small" title="Responsável financeiro">
              <Descriptions size="small" column={1}>
                <Descriptions.Item label="Nome">{contratoPreviewData.nome_responsavel || '-'}</Descriptions.Item>
                <Descriptions.Item label="CPF">{formatCPF(contratoPreviewData.cpf_responsavel) || '-'}</Descriptions.Item>
                <Descriptions.Item label="Email">{contratoPreviewData.email_responsavel || '-'}</Descriptions.Item>
                <Descriptions.Item label="Telefone">{contratoPreviewData.telefone_responsavel || '-'}</Descriptions.Item>
                <Descriptions.Item label="Endereço">{formatEnderecoResponsavelPreview(contratoPreviewData)}</Descriptions.Item>
              </Descriptions>
            </Card>

            <Card size="small" title="Dados da matrícula">
              <Descriptions size="small" column={1}>
                <Descriptions.Item label="Matrícula">{contratoPreviewData.matricula_id || '-'}</Descriptions.Item>
                <Descriptions.Item label="Ano letivo">{contratoPreviewData.ano_letivo || '-'}</Descriptions.Item>
                <Descriptions.Item label="Unidade">{contratoPreviewData.nome_unidade || '-'}</Descriptions.Item>
                <Descriptions.Item label="Turma">{contratoPreviewData.nome_turma || '-'}</Descriptions.Item>
                <Descriptions.Item label="Modalidade">{contratoPreviewData.nome_modalidade || '-'}</Descriptions.Item>
              </Descriptions>
            </Card>
          </Space>
        )}
      </Modal>

      <Modal
        title={`Gerar Contrato - Matrícula #${matriculaContratoSelecionada?.id || ''}`}
        open={contratoModalVisible}
        onOk={handleGerarContrato}
        onCancel={() => {
          setContratoModalVisible(false);
          setMatriculaContratoSelecionada(null);
        }}
        okText="Gerar Contrato"
        cancelText="Cancelar"
        confirmLoading={gerarContratoLoading}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Typography.Text>
            Selecione o template para geração manual do contrato no D4Sign.
          </Typography.Text>
          <Select
            value={contratoTemplateSelecionado}
            onChange={(value) => setContratoTemplateSelecionado(value)}
            options={CONTRATO_TEMPLATE_OPTIONS}
            style={{ width: '100%' }}
          />
        </Space>
      </Modal>

      <Modal
        title={`Alterar turma - Matrícula #${matriculaTrocaSelecionada?.id || ''}`}
        open={trocaVigenciaModalVisible}
        onCancel={fecharModalTrocaVigencia}
        onOk={() => formTrocaVigencia.submit()}
        okText="Aplicar Troca"
        cancelText="Cancelar"
        confirmLoading={trocaVigenciaLoading}
        destroyOnClose
      >
        <Form
          form={formTrocaVigencia}
          layout="vertical"
          onFinish={handleTrocaVigenciaSubmit}
        >
          <Form.Item
            name="id_turma"
            label="Nova Turma"
            rules={[{ required: true, message: 'Turma é obrigatória' }]}
          >
            <Select placeholder="Selecione a turma">
              {getTurmasPorUnidade(matriculaTrocaSelecionada?.id_unidades).map((turma) => (
                <Select.Option key={turma.id} value={turma.id}>
                  {turma.nome_turm}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item noStyle shouldUpdate>
                {() => {
                  const isMensalidade = ((matriculaTrocaSelecionada?.tipo_matricula || 'ANUAL').toUpperCase() === 'MENSALIDADE');
                  return isMensalidade ? (
                    <Form.Item
                      name="valor_mensal"
                      label="Valor Mensal (R$)"
                      rules={[{ required: true, message: 'Valor mensal é obrigatório' }]}
                    >
                      <InputNumber min={0} step={0.01} precision={2} style={{ width: '100%' }} />
                    </Form.Item>
                  ) : (
                    <Form.Item
                      name="valor_total"
                      label="Valor Total (R$)"
                      rules={[{ required: true, message: 'Valor total é obrigatório' }]}
                    >
                      <InputNumber min={0} step={0.01} precision={2} style={{ width: '100%' }} />
                    </Form.Item>
                  );
                }}
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item noStyle shouldUpdate>
                {() => {
                  const isMensalidade = ((matriculaTrocaSelecionada?.tipo_matricula || 'ANUAL').toUpperCase() === 'MENSALIDADE');
                  return isMensalidade ? (
                    <Form.Item label="Competências (calculado automaticamente)">
                      <InputNumber
                        value={trocaQtdCompetenciasCalculada && trocaQtdCompetenciasCalculada > 0 ? trocaQtdCompetenciasCalculada : null}
                        readOnly
                        disabled
                        min={1}
                        precision={0}
                        style={{ width: '100%' }}
                      />
                    </Form.Item>
                  ) : (
                    <Form.Item
                      name="qtd_parcelas_restantes"
                      label="Quantidade de Parcelas Restantes"
                      rules={[{ required: true, message: 'Quantidade de parcelas restantes é obrigatória' }]}
                    >
                      <InputNumber min={1} precision={0} style={{ width: '100%' }} />
                    </Form.Item>
                  );
                }}
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="desconto_percentual"
                label="Desconto Percentual (%)"
                rules={[{ required: true, message: 'Desconto percentual é obrigatório' }]}
              >
                <InputNumber min={0} max={100} step={0.01} precision={2} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="data_inicio"
                label="Data de Início da Vigência"
                rules={[{ required: true, message: 'Data de início é obrigatória' }]}
              >
                <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item noStyle shouldUpdate>
            {() => {
              const isMensalidade = ((matriculaTrocaSelecionada?.tipo_matricula || 'ANUAL').toUpperCase() === 'MENSALIDADE');
              if (!isMensalidade) return null;
              return (
                <Row gutter={16}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="data_fim"
                      label="Data de Fim da Vigência"
                      rules={[{ required: true, message: 'Data de fim é obrigatória para mensalidade' }]}
                    >
                      <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                </Row>
              );
            }}
          </Form.Item>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item label="Valor da Parcela (calculado)">
                <InputNumber
                  value={valorParcelaCalculadoTroca}
                  readOnly
                  disabled
                  min={0}
                  step={0.01}
                  precision={2}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Parcela Final (calculada)">
                <InputNumber
                  value={parcelaFinalCalculadaTroca}
                  readOnly
                  disabled
                  min={0}
                  step={0.01}
                  precision={2}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      <Modal
        title={`Corrigir vigência inicial - Matrícula #${matriculaCorrecaoSelecionada?.id || ''}`}
        open={corrigirVigenciaInicialModalVisible}
        onCancel={fecharModalCorrigirVigenciaInicial}
        onOk={() => formCorrigirVigenciaInicial.submit()}
        okText="Aplicar correção"
        cancelText="Cancelar"
        confirmLoading={corrigirVigenciaInicialLoading}
        destroyOnClose
      >
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Correção operacional controlada"
          description="Use este fluxo apenas para corrigir a primeira vigência já persistida. A operação pode ser bloqueada quando houver histórico financeiro consolidado."
        />

        <Form
          form={formCorrigirVigenciaInicial}
          layout="vertical"
          onFinish={handleCorrigirVigenciaInicialSubmit}
        >
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="data_inicio"
                label="Data de início"
                rules={[{ required: true, message: 'Data de início é obrigatória' }]}
              >
                <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="data_fim" label="Data de fim">
                <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Divider style={{ marginTop: 8, marginBottom: 12 }}>Campos opcionais</Divider>

          <Form.Item name="id_turma" label="Turma (opcional)">
            <Select allowClear placeholder="Manter turma atual da vigência inicial">
              {getTurmasPorUnidade(matriculaCorrecaoSelecionada?.id_unidades).map((turma) => (
                <Select.Option key={turma.id} value={turma.id}>
                  {turma.nome_turm}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="valor_total" label="Valor total (opcional)">
                <InputNumber min={0} step={0.01} precision={2} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="desconto_percentual" label="Desconto percentual (opcional)">
                <InputNumber min={0} max={100} step={0.01} precision={2} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      <Modal
        title={`Alterar vencimento futuro - Matrícula #${matriculaVencimentoSelecionada?.id || ''}`}
        open={alterarVencimentoModalVisible}
        onCancel={fecharModalAlterarVencimento}
        onOk={() => formAlterarVencimento.submit()}
        okText="Aplicar alteração"
        cancelText="Cancelar"
        confirmLoading={alterarVencimentoLoading}
        destroyOnClose
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="A alteração vale apenas para o futuro"
          description="Selecione a partir de qual mês o novo vencimento será aplicado às parcelas futuras. Parcelas já pagas não serão alteradas."
        />

        <Typography.Paragraph type="secondary" style={{ marginBottom: 16 }}>
          Dia de vencimento atual: <strong>{matriculaVencimentoSelecionada?.dia_vencimento ?? '-'}</strong>
        </Typography.Paragraph>

        <Form
          form={formAlterarVencimento}
          layout="vertical"
          onFinish={handleAlterarVencimentoSubmit}
        >
          <Form.Item
            name="novo_dia_vencimento"
            label="Novo dia de vencimento"
            rules={[{ required: true, message: 'Informe o novo dia de vencimento' }]}
          >
            <InputNumber min={1} max={31} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="data_inicio_aplicacao"
            label="Aplicar novo vencimento a partir de"
            help="Informe o mês inicial para aplicar o novo vencimento nas parcelas futuras."
            rules={[{ required: true, message: 'Informe a data de início da aplicação' }]}
          >
            <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`Detalhes da Matrícula #${matriculaDetalhes?.matricula?.id || ''}`}
        open={detalhesModalVisible}
        onCancel={closeDetalhesModal}
        footer={null}
        width={1180}
        destroyOnClose
      >
        {detalhesLoading ? (
          <Typography.Text>Carregando detalhes...</Typography.Text>
        ) : !matriculaDetalhes ? (
          <Empty description="Detalhes indisponíveis" />
        ) : (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Card size="small" title="Dados atuais da matrícula">
              <Descriptions size="small" column={3}>
                <Descriptions.Item label="Aluno">
                  {matriculaDetalhes?.matricula?.aluno?.nome || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Status">
                  {matriculaDetalhes?.matricula?.status || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Turma atual">
                  {matriculaDetalhes?.matricula?.turma_atual?.nome || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Valor atual">
                  {formatCurrency(matriculaDetalhes?.matricula?.valor_atual)}
                </Descriptions.Item>
                <Descriptions.Item label="Dia de vencimento">
                  {matriculaDetalhes?.matricula?.dia_vencimento ?? '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Quantidade de parcelas">
                  {matriculaDetalhes?.matricula?.qtd_parcelas ?? '-'}
                </Descriptions.Item>
              </Descriptions>
            </Card>

            <Card size="small" title="Histórico de vigências">
              <Table
                columns={vigenciasDetalheColumns}
                dataSource={matriculaDetalhes?.vigencias || []}
                rowKey={(record) => record.id}
                pagination={{ pageSize: 6, showSizeChanger: false }}
                size="small"
                locale={{
                  emptyText: <Empty description="Sem histórico de vigências" />,
                }}
                scroll={{ x: 900 }}
              />
            </Card>

            <Card size="small" title="Parcelas da matrícula">
              <Table
                columns={parcelasDetalheColumns}
                dataSource={matriculaDetalhes?.parcelas || []}
                rowKey={(record) => record.id}
                pagination={{ pageSize: 8, showSizeChanger: false }}
                size="small"
                locale={{
                  emptyText: <Empty description="Sem parcelas para esta matrícula" />,
                }}
                scroll={{ x: 980 }}
              />
            </Card>
          </Space>
        )}
      </Modal>

      <Modal
        title={`Parcelas da Matrícula #${matriculaSelecionada?.id || ''}`}
        open={parcelasModalVisible}
        onCancel={closeParcelasModal}
        footer={null}
        width={1000}
        destroyOnClose
      >
        {matriculaSelecionada ? (
          <>
            <Card
              size="small"
              style={{ marginBottom: 16, background: '#f6ffed', borderColor: '#b7eb8f' }}
              bodyStyle={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap' }}
            >
              <div style={{ minWidth: 200 }}>
                <Typography.Text type="secondary">Parcelas esperadas</Typography.Text>
                <div style={{ fontSize: 18, fontWeight: 600 }}>{totalParcelasEsperadas}</div>
              </div>
              <div style={{ minWidth: 200 }}>
                <Typography.Text type="secondary">Parcelas geradas</Typography.Text>
                <div style={{ fontSize: 18, fontWeight: 600 }}>{totalParcelasGeradas}</div>
              </div>
              <div style={{ minWidth: 200 }}>
                <Typography.Text type="secondary">Faltantes</Typography.Text>
                <div style={{ fontSize: 18, fontWeight: 600, color: parcelasPendentes > 0 ? '#cf1322' : '#389e0d' }}>
                  {parcelasPendentes}
                </div>
              </div>
            </Card>

            <Space style={{ marginBottom: 16 }} wrap>
              <Tooltip title="Sincroniza status das parcelas pelas faturas vinculadas (Fatura -> Parcela)">
                <Button
                  type="default"
                  loading={parcelasActionLoading}
                  onClick={handleSincronizarParcelasComFaturas}
                >
                  Sincronizar parcelas com faturas
                </Button>
              </Tooltip>
              <Tooltip title="Recalcular cria apenas as parcelas faltantes">
                <Button
                  icon={<ReloadOutlined />}
                  loading={parcelasActionLoading}
                  onClick={handleRecalcularParcelas}
                  type="primary"
                >
                  Recalcular parcelas
                </Button>
              </Tooltip>
              <Tooltip title="Remove parcelas em aberto quando não houver fatura vinculada impeditiva">
                <Button
                  icon={<DeleteOutlined />}
                  danger
                  onClick={handleBulkDeleteParcelas}
                  disabled={!parcelas.length || parcelasActionLoading}
                  loading={parcelasActionLoading}
                >
                  Apagar todas as parcelas
                </Button>
              </Tooltip>
            </Space>

            {hasDescontoModal && (
              <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                Desconto {descontoModalValor.toFixed(2)}% até o vencimento (ASAAS).
              </Typography.Text>
            )}

            <Table
              columns={parcelaColumns}
              dataSource={parcelas}
              rowKey="id"
              loading={parcelasLoading}
              pagination={{ pageSize: 8, showSizeChanger: false }}
              locale={{
                emptyText: parcelasLoading ? ' ' : <Empty description="Nenhuma parcela gerada" />,
              }}
              scroll={{ x: 700 }}
            />
          </>
        ) : (
          <Empty description="Selecione uma matrícula para visualizar as parcelas" />
        )}
      </Modal>

      <Modal
        title={editingId ? 'Editar Matrícula' : 'Nova Matrícula'}
        open={isModalVisible}
        onOk={() => form.submit()}
        onCancel={() => setIsModalVisible(false)}
        width={1000}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          onValuesChange={handleValuesChange}
        >
          {/* Informações Básicas */}
          <Divider>Informações Básicas</Divider>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="ano_letivo"
                label="Ano Letivo"
                rules={[{ required: true, message: 'Ano letivo é obrigatório' }]}
              >
                <Input placeholder="2024" maxLength={4} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="id_alunos"
                label="Aluno"
                rules={[{ required: true, message: 'Aluno é obrigatório' }]}
              >
                <Select
                  placeholder="Selecione o aluno"
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {alunosOrdenados.map((aluno) => (
                    <Select.Option key={aluno.id} value={aluno.id}>
                      {aluno.nome_aluno}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {user?.role !== 'RESPONSAVEL' && (
            <Row gutter={16}>
              <Col xs={24} sm={12} md={8}>
                <Form.Item
                  name="status"
                  label="Status"
                  rules={[{ required: true, message: 'Status é obrigatório' }]}
                >
                  <Select placeholder="Selecione o status">
                    {MATRICULA_STATUS_OPTIONS.map((option) => (
                      <Select.Option key={option.value} value={option.value}>
                        {option.label}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          )}

          <Form.Item noStyle shouldUpdate={(prev, curr) => prev.id_alunos !== curr.id_alunos}>
            {({ getFieldValue }) => {
              const alunoId = getFieldValue('id_alunos');
              const aluno = alunos.find((a) => a.id === alunoId);
              const responsavelCentral = alunoId ? responsavelFinanceiroPorAluno[alunoId] : null;
              const enderecoPartes = [
                responsavelCentral?.endereco,
                responsavelCentral?.numero,
                responsavelCentral?.complemento,
                responsavelCentral?.bairro,
                responsavelCentral?.cep ? `CEP ${responsavelCentral.cep}` : null,
              ].filter(Boolean);
              const enderecoCentral = enderecoPartes.length ? enderecoPartes.join(', ') : ENDERECO_CENTRAL_INDISPONIVEL;

              return (
                <Card size="small" style={{ marginBottom: 16 }}>
                  <div style={{ marginBottom: 8, color: '#ad6800', fontWeight: 500 }}>
                    Dados a serem inseridos no contrato. Informações de responsável/endereço usam somente o cadastro central.
                  </div>
                  <Descriptions column={2} size="small">
                    <Descriptions.Item label="Data de Nascimento">
                      {aluno?.data_nascimento ? dayjs(aluno.data_nascimento).format('DD/MM/YYYY') : '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Série">
                      {getSerieName(aluno?.id_serie)}
                    </Descriptions.Item>
                    <Descriptions.Item label="Endereço do Responsável">
                      {enderecoCentral}
                    </Descriptions.Item>
                    <Descriptions.Item label="Número">
                      {responsavelCentral?.numero || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Complemento">
                      {responsavelCentral?.complemento || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Bairro">
                      {responsavelCentral?.bairro || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="CEP">
                      {responsavelCentral?.cep || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Responsável Financeiro">
                      {responsavelCentral?.nome || RESPONSAVEL_CENTRAL_INDISPONIVEL}
                    </Descriptions.Item>
                    <Descriptions.Item label="CPF do Responsável">
                      {formatCPF(responsavelCentral?.cpf) || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="E-mail do Responsável">
                      {responsavelCentral?.email || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Telefone do Responsável">
                      {responsavelCentral?.telefone || '-'}
                    </Descriptions.Item>
                  </Descriptions>
                </Card>
              );
            }}
          </Form.Item>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="id_unidades"
                label="Unidade"
                rules={[{ required: true, message: 'Unidade é obrigatória' }]}
              >
                <Select placeholder="Selecione a unidade">
                  {unidades.map((unidade) => (
                    <Select.Option key={unidade.id} value={unidade.id}>
                      {unidade.nome_fantasia}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => prevValues.id_unidades !== currentValues.id_unidades}>
                {({ getFieldValue }) => {
                  const unidadeId = getFieldValue('id_unidades');
                  const turmasFiltradas = getTurmasPorUnidade(unidadeId);

                  return (
                    <Form.Item
                      name="id_turma"
                      label="Turma"
                      rules={[{ required: true, message: 'Turma é obrigatória' }]}
                    >
                      <Select placeholder={unidadeId ? 'Selecione a turma' : 'Selecione a unidade primeiro'} disabled={!unidadeId}>
                        {turmasFiltradas.map((turma) => (
                          <Select.Option key={turma.id} value={turma.id}>
                            {turma.nome_turm}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  );
                }}
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) =>
              prevValues.id_unidades !== currentValues.id_unidades
            }
          >
            {({ getFieldValue }) => {
              const unidadeId = getFieldValue('id_unidades');
              const seriesFiltradas = getSeriesPorUnidade(unidadeId);

              return (
                <Row gutter={16}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="id_serie"
                      label="Série"
                    >
                      <Select
                        placeholder={unidadeId ? 'Selecione a série' : 'Selecione a unidade primeiro'}
                        allowClear
                        showSearch
                        optionFilterProp="children"
                        disabled={!unidadeId}
                        filterOption={(input, option) =>
                          option.children.toLowerCase().includes(input.toLowerCase())
                        }
                      >
                        {seriesFiltradas.map((serie) => (
                          <Select.Option key={serie.id} value={serie.id}>
                            {serie.nome_serie}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
              );
            }}
          </Form.Item>

          {/* Datas */}
          <Divider>Datas</Divider>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="data_inicio" label="Data de Início">
                <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="data_fim" label="Data de Fim">
                <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          {editingId && (
            <Form.Item style={{ marginTop: -8, marginBottom: 12 }}>
              <Space direction="vertical" size={0}>
                <Button
                  type="link"
                  size="small"
                  icon={<ExclamationCircleOutlined />}
                  onClick={abrirCorrecaoVigenciaInicialNaEdicao}
                  style={{ paddingLeft: 0 }}
                >
                  Corrigir vigência inicial
                </Button>
                <Typography.Text type="secondary">
                  Use apenas para corrigir a primeira vigência já salva.
                </Typography.Text>
              </Space>
            </Form.Item>
          )}

          <Form.Item name="tipo_matricula" label="Tipo de Matrícula" initialValue="ANUAL">
            <Select>
              <Select.Option value="ANUAL">ANUAL</Select.Option>
              <Select.Option value="MENSALIDADE">MENSALIDADE</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item 
            name="dia_vencimento" 
            label="Dia de Vencimento"
            help="Dia do mês para vencimento (1-31)"
          >
            <InputNumber min={1} max={31} placeholder="Ex: 10" style={{ width: '100%' }} />
          </Form.Item>

          {editingId && (
            <Form.Item style={{ marginTop: -8, marginBottom: 12 }}>
              <Space direction="vertical" size={0}>
                <Button
                  type="link"
                  size="small"
                  onClick={() => abrirModalAlterarVencimento()}
                  style={{ paddingLeft: 0 }}
                  disabled={!podeAlterarVencimentoNaEdicao}
                >
                  Alterar vencimento futuro
                </Button>
                <Typography.Text type="secondary">
                  {podeAlterarVencimentoNaEdicao
                    ? `Dia atual salvo: ${form.getFieldValue('dia_vencimento') ?? '-'}`
                    : 'Ação disponível apenas para matrículas ATIVAS.'}
                </Typography.Text>
              </Space>
            </Form.Item>
          )}

          {/* Pagamento */}
          <Divider>Pagamento</Divider>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="forma_pgto" label="Forma de Pagamento">
                <Select placeholder="Selecione a forma de pagamento">
                  <Select.Option value="DINHEIRO">Dinheiro</Select.Option>
                  <Select.Option value="CARTAO_CREDITO">Cartão de Crédito</Select.Option>
                  <Select.Option value="CARTAO_DEBITO">Cartão de Débito</Select.Option>
                  <Select.Option value="BOLETO">Boleto</Select.Option>
                  <Select.Option value="PIX">PIX</Select.Option>
                  <Select.Option value="TRANSFERENCIA">Transferência</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => prevValues.tipo_matricula !== currentValues.tipo_matricula}>
                {({ getFieldValue }) => {
                  const tipo = (getFieldValue('tipo_matricula') || 'ANUAL').toUpperCase();
                  const isMensalidade = tipo === 'MENSALIDADE';
                  return (
                    <>
                      <Form.Item name="valor_total" label="Valor Total (R$)">
                        <InputNumber min={0} step={0.01} precision={2} placeholder="0.00" style={{ width: '100%' }} disabled={isMensalidade} />
                      </Form.Item>
                      {isMensalidade && (
                        <Form.Item
                          name="valor_mensal"
                          label="Valor Mensal (R$)"
                          rules={[{ required: true, message: 'Valor mensal é obrigatório para MENSALIDADE' }]}
                        >
                          <InputNumber min={0} step={0.01} precision={2} placeholder="0.00" style={{ width: '100%' }} />
                        </Form.Item>
                      )}
                    </>
                  );
                }}
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="qtd_parcelas" label="Quantidade de Parcelas">
                <InputNumber min={1} placeholder="1" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item 
                name="valor_parcela" 
                label="Valor da Parcela (R$)"
                help="Calculado automaticamente: Valor Total / Quantidade de Parcelas"
              >
                <InputNumber 
                  min={0} 
                  step={0.01} 
                  precision={2} 
                  placeholder="0.00" 
                  style={{ width: '100%' }}
                  disabled 
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="desconto" label="Desconto (%)" help="Desconto percentual (0-100)">
                <InputNumber min={0} max={100} step={0.01} precision={2} placeholder="0.00" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item 
                name="parcela_final" 
                label="Parcela Final (R$)"
                help="Calculado automaticamente: Valor da Parcela - (Valor da Parcela * Desconto / 100)"
              >
                <InputNumber 
                  min={0} 
                  step={0.01} 
                  precision={2} 
                  placeholder="0.00" 
                  style={{ width: '100%' }}
                  disabled 
                />
              </Form.Item>
            </Col>
          </Row>

          {/* Observações */}
          <Divider>Observações</Divider>

          <Form.Item name="obs_financeiro" label="Observações Financeiras">
            <Input.TextArea rows={3} placeholder="Digite observações..." />
          </Form.Item>

        </Form>
      </Modal>
    </div>
  );
};

export default MatriculasList;
